'use client';

import { cn } from '@bells/core/lib/utils';
import { crudNotFound, selectFilter } from '@bells/i18n';
import { isEmpty } from 'lodash';
import { Check } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';

import {
  type FocusEventHandler,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

export type Options = {
  [value: string]: string;
};
export type Values = {
  label: string;
  value: string;
  icon?: ReactNode;
  description?: string;
}[];

export type SelectionListProps = {
  onChange: (status: string | null) => void;
  options: Options | Values;
  disabled?: boolean;
  value?: string | null;
  searchTerm?: string;
  notFoundLabel?: string;
  hasSearch?: boolean;
  ordered?: boolean;
  isMobileOnlySelection?: boolean;
  virtualHeight?: number;
  isOptional?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
};

function SelectionListVirtual({
  onChange,
  isOptional,
  options,
  disabled,
  value,
  searchTerm,
  notFoundLabel,
  hasSearch,
  ordered = true,
  isMobileOnlySelection,
  virtualHeight,
}: SelectionListProps) {
  const searchTermValue = useMemo(() => {
    return searchTerm ?? selectFilter();
  }, [searchTerm]);

  const notFoundLabelValue = useMemo(() => {
    return notFoundLabel ?? crudNotFound();
  }, [notFoundLabel]);

  const [search, setSearch] = useState('');

  const parentRef = useRef<HTMLDivElement | null>(null);

  const availableOptions = useMemo(() => {
    if (options === null || options === undefined) {
      return [];
    }

    let finalOptions: Values = [];
    if (options instanceof Array) {
      finalOptions = options;
    } else {
      finalOptions = Object.entries(options).map(([key, label]) => {
        return {
          value: key,
          label: label,
          icon: undefined,
          description: undefined,
        };
      });
    }

    if (!isEmpty(search)) {
      finalOptions = finalOptions.filter((df) =>
        df.label.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (ordered) {
      return finalOptions.sort((a, b) => a.label.localeCompare(b.label));
    } else {
      return finalOptions;
    }
  }, [options, search]);

  const selectedIndex = useMemo(() => {
    return availableOptions.findIndex((item) => item.value === value);
  }, [availableOptions, value]);

  const finalHeight = virtualHeight ?? 30;
  const rowVirtualizer = useVirtualizer({
    count: availableOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => finalHeight,
    overscan: 5,
    initialOffset: selectedIndex * finalHeight,
  });
  const hasScrolledRef = useRef(false);

  useLayoutEffect(() => {
    if (hasScrolledRef.current || selectedIndex < 0 || !parentRef.current) {
      return;
    }

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        rowVirtualizer.scrollToIndex(selectedIndex, { align: 'center' });
        hasScrolledRef.current = true;
      });
    });

    return () => cancelAnimationFrame(id);
  }, [selectedIndex, rowVirtualizer, parentRef.current]);

  return (
    <Command shouldFilter={false}>
      {hasSearch && (
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder={searchTermValue}
        />
      )}

      <CommandList
        ref={parentRef}
        className={cn({ 'max-h-max flex-1': isMobileOnlySelection })}
      >
        <CommandEmpty className='p-4'>{notFoundLabelValue}</CommandEmpty>
        <CommandGroup>
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = availableOptions[virtualRow.index];

              return (
                item && (
                  <div
                    key={item.value}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transform: `translateY(${virtualRow.start}px)`,
                      height: `${virtualRow.size}px`,
                      width: '100%',
                    }}
                  >
                    <CommandItem
                      value={item.value}
                      disabled={disabled}
                      onSelect={(val) => {
                        if (isOptional) {
                          onChange?.(val == value ? null : val);
                        } else {
                          onChange?.(val);
                        }
                      }}
                      className={cn({
                        'bg-muted': item.value === value,
                      })}
                    >
                      <div className='flex w-full flex-col'>
                        <div className='flex items-center gap-1 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'>
                          {item.icon}
                          <span className='block w-0 flex-1 truncate'>
                            {item.label}
                          </span>
                        </div>
                      </div>
                      <Check
                        className={cn(
                          'ml-auto',
                          item.value === value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  </div>
                )
              );
            })}
          </div>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function SelectionListDefault({
  onChange,
  isOptional,
  options,
  disabled,
  value,
  searchTerm,
  notFoundLabel,
  hasSearch,
  ordered = true,
  isMobileOnlySelection,
  onBlur,
}: SelectionListProps) {
  const [search, setSearch] = useState('');

  const availableOptions = useMemo(() => {
    if (options === null || options === undefined) {
      return [];
    }

    let finalOptions: Values = [];
    if (options instanceof Array) {
      finalOptions = options;
    } else {
      finalOptions = Object.entries(options).map(([key, label]) => {
        return {
          value: key,
          label: label,
          icon: undefined,
          description: undefined,
        };
      });
    }

    if (!isEmpty(search)) {
      finalOptions = finalOptions.filter((df) =>
        df.label.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (ordered) {
      return finalOptions.sort((a, b) => a.label.localeCompare(b.label));
    } else {
      return finalOptions;
    }
  }, [options, search]);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    if (selectedRef.current && !hasScrolledRef.current) {
      selectedRef.current.scrollIntoView({ block: 'start' });
      hasScrolledRef.current = true;
    }
  }, [selectedRef?.current]);

  //isMultiple

  return (
    <Command shouldFilter={false}>
      {hasSearch && (
        <CommandInput
          onBlur={onBlur}
          disabled={disabled}
          value={search}
          onValueChange={setSearch}
          placeholder={searchTerm}
        />
      )}

      <CommandList
        className={cn({ 'max-h-max flex-1': isMobileOnlySelection })}
      >
        <CommandEmpty className='p-4'>{notFoundLabel}</CommandEmpty>
        <CommandGroup>
          {availableOptions.map((item) => (
            <CommandItem
              key={item.value}
              disabled={disabled}
              ref={item.value === value ? selectedRef : undefined}
              value={item.value}
              onSelect={(val) => {
                if (isOptional) {
                  onChange?.(val == value ? null : val);
                } else {
                  onChange?.(val);
                }
              }}
            >
              <div className='flex w-full flex-col'>
                <div className='flex items-center gap-1 truncate [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'>
                  {item.icon}
                  {item.label}
                </div>
                {item.description && (
                  <div className='text-muted-foreground'>
                    {item.description}
                  </div>
                )}
              </div>
              <Check
                className={cn(
                  'ml-auto',
                  item.value === value ? 'opacity-100' : 'opacity-0'
                )}
              ></Check>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function SelectionList({
  isVirtual,
  ...props
}: SelectionListProps & { isVirtual?: boolean }) {
  return isVirtual ? (
    <SelectionListVirtual {...props}></SelectionListVirtual>
  ) : (
    <SelectionListDefault {...props}></SelectionListDefault>
  );
}

export { SelectionList };
